'use client'

import { useMutation } from '@apollo/client/react'
import {
  Checkbox,
  CheckboxProvider,
  Form,
  FormError,
  FormInput,
  FormLabel,
  FormProvider,
  FormSubmit,
  useCheckboxContext,
  useFormContext,
} from '@ariakit/react'
import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import {
  Eye,
  MediaAsContents,
  SubmitButton,
  TextLink,
  TypeStyle,
  TypeStyleBodyMicro,
  TypeStyleHeadlineExtraSmall,
  TypeStyleHeadlineSmall,
  focusOutline,
  fromMd,
  untilMd,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../../__generated__/graphql/catalog'
import { MutationStatus } from '../../../../lib/hooks/types'
import { useAuthentication } from '../../../_providers/AuthenticationProvider.client'
import { useGraphqlClients } from '../../../_providers/GraphqlClientsProvider.client'
import { useTranslation } from '../../../_providers/TranslationProvider.client'
import { reportClientError } from '../../chrome/scripts/DataDogRumScript'
import { PageSection } from '../PageSection.client'

import {
  AccountSignupTypography,
  CancelButton,
  Error,
  ErrorToastMessage,
  FormInputComponents,
  FormLabelTypography,
  GrayHorizontalBar,
  GreenMessage,
  MaxWidthContainer,
  RegisterButton,
} from './Account.client'

export const SplitPageSection = styled(PageSection)`
  ${fromMd} {
    display: flex;
  }
  position: relative;
  ${untilMd} {
    background-position: top;
    background-image: url('https://images.ctfassets.net/5j6wpslh72e4/uqsnzVq2l6VvoFl3eBlyX/7fd4644b0d65e844b3fc9a18f78b03d6/Login_Flows_Refresh_Desktop_Asset.jpg?w=812');
    padding: ${o => o.theme.spacing(5, 4)};
    background-size: cover;
  }
`

export const LoginContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  ${untilMd} {
    width: 100%;
    background: ${o => o.theme.color.fill.background};
    border-radius: 8px;
    box-shadow: 0 8px 8px 0 rgba(0, 0, 0, 0.1);
    padding: ${o => o.theme.spacing(5, 0, 0, 0)};
  }

  ${fromMd} {
    width: 50%;
    padding: ${o => o.theme.spacing(10)};
    position: relative;
  }
  align-items: center;
  justify-content: center;
  gap: ${o => o.theme.spacing(5)};

  margin: auto;
`

export const LoginContent: React.FC<{ redirectUrl?: string }> = ({ redirectUrl }) => {
  const [currentContent, setCurrentContent] = useState<
    'login' | 'forgot-password' | 'login-just-reset'
  >('login')
  const translations = useTranslation()
  const [defaultForgottenEmailAddress, setDefaultForgottenEmailAddress] = useState('')
  return (
    <MaxWidthContainer>
      {(currentContent === 'login' || currentContent === 'login-just-reset') && (
        <>
          <TitlesWrapper>
            <TypeStyleHeadlineSmall>
              {translations.magnolia.account.login.welcomeBack}
            </TypeStyleHeadlineSmall>
          </TitlesWrapper>
          <FormProvider defaultValues={{ 'email': '', 'password': '', 'keep-logged-in': true }}>
            <LoginForm
              setCurrentContent={setCurrentContent}
              setDefaultForgottenEmailAddress={setDefaultForgottenEmailAddress}
              currentContent={currentContent}
              redirectUrl={redirectUrl}
            />
          </FormProvider>
          <MediaAsContents greaterThan='md'>
            <GrayHorizontalBar />
          </MediaAsContents>
          <CreateAccountTile>
            <TypeStyleHeadlineExtraSmall>
              {translations.magnolia.account.login.newToFigs}
            </TypeStyleHeadlineExtraSmall>
            <AccountSignupTypography isUppercase={true}>
              <RegisterButton
                href={'/account/register'}
                inheritTypography
                variant={'black-on-transparent'}
                forwardedAs={'a'}
              >
                {translations.magnolia.account.login.createAnAccount.toUpperCase()}
              </RegisterButton>
            </AccountSignupTypography>
          </CreateAccountTile>
        </>
      )}
      {currentContent === 'forgot-password' && (
        <>
          <TitlesWrapper>
            <TypeStyleHeadlineSmall>
              {translations.magnolia.general.resetYourPassword}
            </TypeStyleHeadlineSmall>
            <TypeStyleBodyMicro>
              {translations.magnolia.general.passwordResetInstructions}
            </TypeStyleBodyMicro>
          </TitlesWrapper>
          <FormProvider defaultValues={{ 'forgotten-email': defaultForgottenEmailAddress }}>
            <ResetPasswordForm setCurrentContent={setCurrentContent} />
          </FormProvider>
          <MediaAsContents greaterThan='md'>
            <GrayHorizontalBar></GrayHorizontalBar>
          </MediaAsContents>
          <CancelButton onClick={() => setCurrentContent('login')} variant='black-on-gray'>
            {translations.magnolia.general.cancel}
          </CancelButton>
        </>
      )}
    </MaxWidthContainer>
  )
}

const LoginForm = ({
  setCurrentContent,
  setDefaultForgottenEmailAddress,
  currentContent,
  redirectUrl,
}: {
  setCurrentContent: React.Dispatch<
    React.SetStateAction<'forgot-password' | 'login' | 'login-just-reset'>
  >
  setDefaultForgottenEmailAddress: React.Dispatch<React.SetStateAction<string>>
  currentContent: 'forgot-password' | 'login' | 'login-just-reset'
  redirectUrl?: string
}) => {
  const { logIn, loginMutationErrors, loginMutationStatus, account } = useAuthentication()
  const submitError = loginMutationErrors.length > 0

  const {
    magnolia: { account: accountStrings, general: generalStrings },
  } = useTranslation()
  const form = useFormContext()
  const [showPassword, setShowPassword] = useState(false)

  const toggleShowPassword = useCallback(() => {
    setShowPassword(!showPassword)
  }, [setShowPassword, showPassword])

  form?.useValidate(() => {
    if (formPasswordValue.length > 40) {
      form.setError('password', generalStrings.maxPasswordLengthValidationMessage)
    }
    if (formPasswordValue.length <= 0) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      form?.setError('password', generalStrings.requiredText)
    }
    if (!formEmailValue.includes('@')) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      form?.setError('email', generalStrings.emailWarning)
    }
    if (formEmailValue.length <= 0) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      form?.setError('email', generalStrings.requiredText)
    }
  })

  const formEmailValue = form?.useValue('email')
  const formPasswordValue = form?.useValue('password')

  const onLoginFormSubmit = useCallback(() => {
    setCurrentContent('login')
    try {
      logIn({
        email: formEmailValue,
        password: formPasswordValue,
        destination: redirectUrl,
      })
    } catch (error) {
      reportClientError({ error, context: { scope: 'magnolia/LoginForm:onSubmit' } })
    }
  }, [logIn, formEmailValue, formPasswordValue, setCurrentContent, redirectUrl])

  //Fill in email with value from local storage if available.
  useEffect(() => {
    if (!form?.getValue('email') && account?.email) {
      form?.setValue('email', account.email)
    }
  }, [account?.email, form])

  return (
    <StyledForm
      method='POST'
      resetOnSubmit={false}
      onSubmit={onLoginFormSubmit}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {submitError && currentContent !== 'login-just-reset' ? (
        <ErrorToastMessage id='login-form-submit-error' role={'alert'}>
          {generalStrings.incorrectCredentials}
        </ErrorToastMessage>
      ) : null}
      <FormInputComponents>
        <FormLabel
          name={'email'}
          render={
            <FormLabelTypography isUppercase>
              {generalStrings.emailLabel.toUpperCase()}
            </FormLabelTypography>
          }
        />

        <FormInput
          name='email'
          render={props => <StyledTextInput {...props} />}
          placeholder={accountStrings.login.emailPlaceholder}
        ></FormInput>
        <FormError
          name={'email'}
          render={props => {
            return <Error {...props} color={undefined} />
          }}
        />
      </FormInputComponents>
      <FormInputComponents>
        <FormLabel
          name={'password'}
          render={
            <FormLabelTypography isUppercase>
              {generalStrings.passwordLabel.toUpperCase()}
            </FormLabelTypography>
          }
        />
        <InputContainer>
          <FormInput
            placeholder={accountStrings.login.passwordPlaceholder}
            type={showPassword ? 'text' : 'password'}
            name='password'
            render={props => <StyledTextInput {...props} />}
          ></FormInput>

          <CheckboxProvider defaultValue={showPassword}>
            <HidePasswordCheckbox toggleShowPassword={toggleShowPassword} />
          </CheckboxProvider>
        </InputContainer>
        <FormError
          name={'password'}
          render={props => {
            return <Error {...props} color={undefined} />
          }}
        />
      </FormInputComponents>

      <ForgotYourPasswordLink>
        <TextLink
          as={'button'}
          type='button'
          onClick={() => {
            setDefaultForgottenEmailAddress(formEmailValue)
            setCurrentContent('forgot-password')
          }}
          inheritTypography
        >
          {generalStrings.forgotYourPassword}
        </TextLink>
      </ForgotYourPasswordLink>
      {currentContent === 'login-just-reset' && (
        <GreenMessage>{accountStrings.login.passwordResetSuccess}</GreenMessage>
      )}
      <FormSubmit
        render={
          <SubmitButtonTall
            status={loginMutationStatus === 'rejected' ? 'idle' : loginMutationStatus}
            pendingLabel={accountStrings.login.loggingIn}
            resolvedLabel={accountStrings.login.loggedIn}
            idleChildren={generalStrings.logIn}
          />
        }
        aria-describedby='login-form-submit-error'
      ></FormSubmit>
    </StyledForm>
  )
}

const ResetPasswordForm = ({
  setCurrentContent,
}: {
  setCurrentContent: React.Dispatch<
    React.SetStateAction<'forgot-password' | 'login' | 'login-just-reset'>
  >
}) => {
  const { personalizedClient } = useGraphqlClients()
  const [forgotPassword, { loading, called, error }] = useMutation(
    gql(`
  mutation customerForgotPassword($email: String!) {
    customerForgotPassword(email: $email)
  }
`),
    { client: personalizedClient }
  )
  const status = useMemo<MutationStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    if (called) return 'resolved'
    return 'idle'
  }, [called, error, loading])
  const form = useFormContext()
  const formEmailValue = form?.useValue('forgotten-email')
  form?.useValidate(() => {
    if (!formEmailValue.includes('@')) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      form?.setError('forgotten-email', generalStrings.emailWarning)
    }
    if (formEmailValue.length <= 0) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      form?.setError('forgotten-email', generalStrings.requiredText)
    }
  })
  const {
    magnolia: { account: accountStrings, general: generalStrings },
  } = useTranslation()
  return (
    <StyledForm
      onSubmit={async () => {
        await forgotPassword({ variables: { email: formEmailValue } })
        setCurrentContent('login-just-reset')
      }}
      validateOnChange={false}
      validateOnBlur={true}
      resetOnSubmit={true}
    >
      <FormInputComponents>
        <FormLabel
          name='forgotten-email'
          render={props => (
            <FormLabelTypography {...props} color={undefined} isUppercase={true}>
              {generalStrings.emailLabel.toUpperCase()}
            </FormLabelTypography>
          )}
        />
        <FormInput
          name='forgotten-email'
          render={props => {
            return <StyledTextInput {...props} />
          }}
          placeholder={accountStrings.login.emailPlaceholder}
        ></FormInput>
        <FormError
          name='forgotten-email'
          render={props => {
            return <Error {...props} color={undefined} />
          }}
        />
      </FormInputComponents>
      <FormSubmit
        render={
          <SubmitButton
            status={status === 'rejected' ? 'idle' : status}
            pendingLabel={accountStrings.reset.submit}
            resolvedLabel={accountStrings.reset.submit}
            idleChildren={accountStrings.reset.submit}
          />
        }
        aria-describedby='reset-password-form-submit-error'
      ></FormSubmit>
      {error && (
        <Error id='reset-password-form-submit-error' role={'alert'}>
          {accountStrings.reset.errorMessage}
        </Error>
      )}
    </StyledForm>
  )
}

export const HidePasswordCheckbox = ({
  toggleShowPassword,
}: {
  toggleShowPassword: () => void
}) => {
  const theme = useTheme()
  const checkbox = useCheckboxContext()
  const {
    magnolia: { account },
  } = useTranslation()
  return (
    <Checkbox
      onChange={() => {
        toggleShowPassword()
      }}
      render={props => (
        <StyledEye
          fill={theme.color.stroke.neutral.text.tertiary.on.background}
          crossedOut={!checkbox?.getState().value}
          {...props}
          aria-label={
            !checkbox?.getState().value ? account.login.showPassword : account.login.hidePassword
          }
        />
      )}
    ></Checkbox>
  )
}

export const CreateAccountTile = styled.div`
  border-radius: 0 0 8px 8px;
  ${fromMd} {
    border-radius: 8px;
  }
  background: ${o => o.theme.color.fill.subtle};
  padding: ${o => o.theme.spacing(5)};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${o => o.theme.spacing(5)};
  box-sizing: border-box;
  text-align: center;
`

export const StyledTextInput = styled.input`
  ${TypeStyle.css.bodySmall}
  background: ${o => o.theme.color.input.empty.fill.on.background};
  border-radius: 4px;
  border: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.strong};
  &[aria-invalid='true'] {
    border: 1px solid ${o => o.theme.color.input.invalid.empty.border.on.background};
  }
  box-sizing: border-box;
  font-weight: 600;
  height: 42px;
  padding: ${o => o.theme.spacing(2, 4)};
  width: 100%;

  &:focus {
    ${focusOutline}
  }

  &::placeholder {
    color: ${o => o.theme.color.text.secondary.on.background};
    ${TypeStyle.css.bodySmall}
    font-weight: 600;
    opacity: 0.8;
  }
  &:autofill {
    ${TypeStyle.css.bodySmall};
    font-weight: 600;
  }
  &:not(:placeholder-shown) {
    border: 1px solid ${o => o.theme.color.stroke.neutral.border.interaction.primary.on.background};
  }
`

export const StyledForm = styled(Form)`
  width: calc(100% - 32px);
  ${fromMd} {
    width: 100%;
  }
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(6)};
`

const ForgotYourPasswordLink = styled(TypeStyleBodyMicro)`
  align-self: flex-end;
  font-weight: 600;
`

export const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(1)};
`

const StyledEye = styled(({ className, ...rest }: ComponentProps<typeof Eye>) => (
  <span className={className}>
    <Eye {...rest} />
  </span>
))`
  position: absolute;
  right: ${o => o.theme.spacing(4)};
  top: 50%;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &:focus-visible {
    ${focusOutline}
    /* Override relative position that is applied by 'focusOutline' partial */
    position: absolute;
  }
  cursor: pointer;
`

const SubmitButtonTall = styled(SubmitButton)`
  height: 50px;
`

const TitlesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
  align-items: center;
  margin: ${o => o.theme.spacing(0, 4)};
`
